import * as React from "react";
import { ResolveUsers, IdentityUser } from "../organizations/IdentityAPI";

/**
 * Turns identity-server user ids into names.
 *
 * Students are not an entity in this database - a "student" is a user id from the identity server,
 * which is why every mark and enrollment payload carries a bare ULID and no name. The old mark grid
 * rendered `row.studentID` straight into the Student column, so a teacher capturing marks read a
 * column of opaque identifiers.
 *
 * Resolution is a separate round trip to the identity server, so it is batched, cached for the
 * lifetime of the page, and always degrades to showing the id rather than blocking the grid.
 */

const cache = new Map<string, string>();
const inFlight = new Map<string, Promise<void>>();

function nameOf(user: Partial<IdentityUser>): string {
    return (
        user.displayName ||
        user.fullName ||
        [user.firstName, user.lastName].filter(Boolean).join(" ") ||
        user.userName ||
        user.email ||
        ""
    );
}

/**
 * The `$resolve` endpoint's response shape is not pinned down by a generated client (it belongs to
 * the identity server, not this API), so both an array of users and an id-keyed map are accepted.
 */
function ingest(payload: unknown) {
    if (Array.isArray(payload)) {
        payload.forEach((user: Partial<IdentityUser>) => {
            const name = nameOf(user);
            if (user.id && name) cache.set(user.id, name);
        });
        return;
    }

    if (payload && typeof payload === "object") {
        Object.entries(payload as Record<string, Partial<IdentityUser> | string>).forEach(([id, value]) => {
            const name = typeof value === "string" ? value : nameOf(value);
            if (name) cache.set(id, name);
        });
    }
}

async function resolve(ids: string[]): Promise<void> {
    const missing = ids.filter((id) => id && !cache.has(id) && !inFlight.has(id));
    if (!missing.length) return;

    const request = ResolveUsers(missing)
        .then(ingest)
        .catch(() => {
            // A directory that is down must not take the mark sheet with it - callers fall back to
            // the raw id. Marking the ids as attempted stops a retry storm on every re-render.
            missing.forEach((id) => {
                if (!cache.has(id)) cache.set(id, "");
            });
        })
        .finally(() => missing.forEach((id) => inFlight.delete(id)));

    missing.forEach((id) => inFlight.set(id, request));
    await request;
}

/** The resolved name, or the id itself when the directory has nothing (or is unreachable). */
export function displayName(id?: string): string {
    if (!id) return "";
    return cache.get(id) || id;
}

/**
 * Resolves every id in the list and re-renders once they land.
 *
 *   const names = useStudentNames(rows.map((r) => r.studentID));
 *   <td>{names(row.studentID)}</td>
 */
export function useStudentNames(ids: Array<string | undefined>): (id?: string) => string {
    const [, setVersion] = React.useState(0);
    const key = ids.filter(Boolean).join(",");

    React.useEffect(() => {
        let cancelled = false;
        const wanted = key ? key.split(",") : [];
        if (!wanted.length) return;

        resolve(wanted).then(() => {
            if (!cancelled) setVersion((v) => v + 1);
        });

        return () => {
            cancelled = true;
        };
    }, [key]);

    return displayName;
}
